import type { JSX } from 'react';

import type { ToolUseItem } from '../../../../contracts/harness.types';
import { friendlyToolError } from '../../../../data/projects/components/projectChatMessage.helpers';
import {
  getWorkHostPort,
  type WorkHostBridgePorts,
} from '../../../work-home/work-home-ports';

import { isRecord, readNumber, readToolData, readToolError } from './shared/readers';
import { toCreateTemplateStatus } from './status';

type WordLibraryPreviousValues = Record<string, string | null>;
type UpdateWordLibraryBlockProps = Parameters<
  WorkHostBridgePorts['UpdateWordLibraryBlock']
>[0];

// eslint-disable-next-line react-refresh/only-export-components -- Adapter exports must keep their existing render function names.
const UpdateWordLibraryToolBlock = (
  props: Omit<UpdateWordLibraryBlockProps, 'onBlockError'>
): JSX.Element => {
  const bridge = getWorkHostPort('bridge');
  const reportBlockError = bridge.useBlockErrorReporter();
  const UpdateWordLibraryBlock = bridge.UpdateWordLibraryBlock;
  return <UpdateWordLibraryBlock {...props} onBlockError={reportBlockError} />;
};

/** Read find→replace pairs from the tool input `entries` object. */
const readEntries = (
  input: Record<string, unknown>
): Array<{ from: string; to: string }> => {
  const raw = input.entries;
  if (!isRecord(raw)) return [];
  return Object.entries(raw)
    .filter(
      (pair): pair is [string, string] =>
        typeof pair[0] === 'string' && typeof pair[1] === 'string'
    )
    .map(([from, to]) => ({ from, to }));
};

/** Optional prior values from tool result `previous` / `previous_values`. */
const readPreviousValues = (
  data: Record<string, unknown> | undefined
): WordLibraryPreviousValues | undefined => {
  if (!data) return undefined;
  const raw = data.previous ?? data.previous_values;
  if (!isRecord(raw)) return undefined;
  const out: WordLibraryPreviousValues = {};
  let any = false;
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'string' || value === null) {
      out[key] = value;
      any = true;
    }
  }
  return any ? out : undefined;
};

export const renderUpdateWordLibraryToolUse = (item: ToolUseItem): JSX.Element | null => {
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const entries = readEntries(item.input);
  const data = readToolData(item);

  if (status === 'error') {
    return (
      <UpdateWordLibraryToolBlock
        status="error"
        entries={entries}
        error={friendlyToolError(readToolError(item))}
        toolId={item.id}
      />
    );
  }

  return (
    <UpdateWordLibraryToolBlock
      status={status}
      entries={entries}
      added={readNumber(data, 'added')}
      updated={readNumber(data, 'updated')}
      previousValues={readPreviousValues(data)}
      toolId={item.id}
    />
  );
};
